"use client";

import { useEffect, useRef, useState } from "react";
import { GripHorizontal } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const highlights = [{
    index: "01",
    title: "Retail Logistics",
    description: "Automated inventory tracking and supply chain synchronization to eliminate manual stock errors.",
    meta: "SCALABLE / 24-7 / INTEGRATED",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/retail_automation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/retail_automation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/retail_automation.png-wvc-sizes",
    alt: "Modern retail warehouse with automated sorting systems"
  }, {
    index: "02",
    title: "Healthcare Admin",
    description: "Secure patient scheduling and automated intake workflows that prioritize data privacy and speed.",
    meta: "HIPAA / 45 MIN / SECURE",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/healthcare_admin.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/healthcare_admin.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/healthcare_admin.webp-wvc-sizes",
    alt: "Digital healthcare dashboard on a tablet interface"
  }, {
    index: "03",
    title: "Financial Services",
    description: "Automated invoicing and payment reconciliation systems designed for high-volume transaction accuracy.",
    meta: "FINTECH / 90 MIN / PRECISE",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/finance_automation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/finance_automation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/finance_automation.png-wvc-sizes",
    alt: "Financial data visualization on a dark screen"
  }, {
    index: "04",
    title: "Real Estate Ops",
    description: "Automated lead nurturing and property viewing scheduling to keep your pipeline moving without effort.",
    meta: "CRM / 25 MIN / RESPONSIVE",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/real_estate_automation.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/real_estate_automation.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/real_estate_automation.webp-wvc-sizes",
    alt: "Modern real estate office with digital property listings"
  }, {
    index: "05",
    title: "E-commerce Support",
    description: "AI-driven chatbots that resolve customer inquiries instantly, reducing support ticket backlogs.",
    meta: "AI-CHAT / 60 MIN / 1-ON-1",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ecommerce_support.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ecommerce_support.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/ecommerce_support.png-wvc-sizes",
    alt: "Customer support agent using AI-assisted tools"
  }, {
    index: "06",
    title: "Professional Services",
    description: "End-to-end workflow automation for agencies, from client onboarding to project delivery.",
    meta: "AGENCY / 7 DAYS / IMMERSION",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/agency_workflow.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/agency_workflow.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/agency_workflow.webp-wvc-sizes",
    alt: "Digital workflow diagram on a clean workspace"
  }];
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        setProgress(0);
        return;
      }
      setProgress(Math.min(100, Math.max(0, el.scrollLeft / max * 100)));
    };
    handleScroll();
    el.addEventListener("scroll", handleScroll, {
      passive: true
    });
    window.addEventListener("resize", handleScroll);
    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
  return <section data-section-id="1289" id="industries-we-transform" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      {}
      <div aria-hidden className="pointer-events-none absolute left-0 right-0 top-0 h-px origin-left bg-foreground/70" style={{
      transform: "rotate(-6deg) translateY(-12px)"
    }} />
      <div aria-hidden className="pointer-events-none absolute right-6 top-0 size-1.5 bg-foreground/70" style={{
      transform: "translateY(-14px)"
    }} />

      {}
      <span aria-hidden className="font-sans pointer-events-none absolute -left-4 top-24 select-none uppercase leading-none text-foreground/[0.04]" style={{
      fontSize: "clamp(18rem, 32vw, 36rem)"
    }}>
        05
      </span>

      {}
      <div ref={headerRef} className={`relative mx-auto max-w-[1440px] px-6 md:px-12 transition-all duration-[520ms] ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-8">
          {}
          <div className="md:col-span-4">
            <div className="mb-6 flex items-center gap-3">
              <span className="font-mono text-[0.75rem] font-medium uppercase tracking-[0.18em] text-foreground/70">
                &sect; 05
              </span>
              <span className="font-mono text-[0.75rem] font-medium uppercase tracking-[0.18em] text-foreground/70">
                / Sectors
              </span>
              <span aria-hidden className="ml-1 h-px w-10 bg-border" />
            </div>
            <h2 className="font-default uppercase text-foreground" style={{
            fontSize: "clamp(3rem, 8vw, 7.5rem)",
            lineHeight: "0.86",
            letterSpacing: "-0.015em"
          }}>
              Industries We{" "}
              <span className="font-serif italic text-foreground/70" style={{
              letterSpacing: "0.01em"
            }}>
                Automate
              </span>
            </h2>
          </div>

          {}
          <div className="md:col-span-8 md:pl-8 md:pt-6 lg:pl-16">
          </div>
        </div>
      </div>

      {}
      <div className="relative mt-16 md:mt-24">
        <div ref={scrollRef} className="flex snap-x snap-mandatory gap-6 overflow-x-auto pb-2 pl-6 pr-6 md:gap-8 md:pl-12 md:pr-0 cursor-grab active:cursor-grabbing" style={{
        scrollbarWidth: "none"
      }}>
          <style>{`#industries-we-transform ::-webkit-scrollbar{display:none}`}</style>

          {highlights.map((item, i) => <article key={item.index} data-index={i} className="group relative shrink-0 snap-start bg-card border-[1.5px] border-foreground/90 transition-[border-color,border-width] duration-[320ms] ease-out hover:border-[2.5px] hover:border-foreground/70" style={{
          width: "min(78vw, 420px)",
          height: "560px"
        }}>
              {}
              <span aria-hidden className="pointer-events-none absolute -left-px -top-px z-10 block border-l-[1.5px] border-t-[1.5px] border-foreground/70 transition-all duration-[320ms] ease-out" style={{
            width: "12px",
            height: "12px"
          }} />
              <span aria-hidden className="pointer-events-none absolute -left-px -top-px z-10 block opacity-0 border-l-[1.5px] border-t-[1.5px] border-foreground/70 transition-opacity duration-[320ms] ease-out group-hover:opacity-100" style={{
            width: "20px",
            height: "20px"
          }} />
              {}
              <span aria-hidden className="pointer-events-none absolute -bottom-px -right-px z-10 block border-b-[1.5px] border-r-[1.5px] border-foreground/70 transition-all duration-[320ms] ease-out" style={{
            width: "12px",
            height: "12px"
          }} />
              <span aria-hidden className="pointer-events-none absolute -bottom-px -right-px z-10 block opacity-0 border-b-[1.5px] border-r-[1.5px] border-foreground/70 transition-opacity duration-[320ms] ease-out group-hover:opacity-100" style={{
            width: "20px",
            height: "20px"
          }} />

              {}
              <div className="relative h-[60%] w-full overflow-hidden bg-foreground/5">
                <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale transition-transform duration-[320ms] ease-out group-hover:scale-[1.03]" />
                <span aria-hidden className="font-mono pointer-events-none absolute left-4 top-4 text-[0.75rem] font-medium uppercase tracking-[0.18em] text-background bg-foreground px-2 py-1">
                  &sect; {item.index}
                </span>
              </div>

              {}
              <div className="flex h-[40%] flex-col justify-between p-6">
                <div>
                  <h3 className="font-default uppercase text-foreground" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                lineHeight: "0.92",
                letterSpacing: "0em"
              }}>
                    {item.title}
                  </h3>
                  <p className="font-default mt-3 text-foreground/80" style={{
                fontSize: "0.9375rem",
                lineHeight: "1.55"
              }}>
                    {item.description}
                  </p>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <span aria-hidden className="h-px w-6 bg-foreground/70" />
                  <span className="font-mono text-[0.75rem] font-medium uppercase tracking-[0.14em] text-foreground/70">
                    {item.meta}
                  </span>
                </div>
              </div>
            </article>)}

          {}
          <div aria-hidden className="shrink-0" style={{
          width: "1px"
        }} />
        </div>

        {}
        <div className="mt-8 px-6 md:px-12">
          <div className="relative h-1.5 w-full bg-foreground/10">
            <div className="absolute left-0 top-0 h-full bg-foreground/70 transition-[width] duration-150 ease-out" style={{
            width: `${progress}%`
          }} />
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-foreground/60">
                Scroll Progress
              </span>
              <span className="font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-foreground/70">
                {String(Math.round(progress)).padStart(3, "0")}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>;
}